import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Dialog} from 'primereact/dialog'
import React, {forwardRef, useImperativeHandle, useMemo} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {actionTableKeys} from '../new-table/Constants'
import StyledECGHistoryDataTable from '../styled-component/ecg/ECGHistoryDataTable'
import {ECGDeviceTypes} from './ECGConstants'
import {usePageElectrocardiograms} from './query-keys/ECGKeysManager'

// const QueryKey = 'Electrocardiogram'
const PATH = 'ecg'

const defaultShownColumns = [
    {key: 'createdAt'},
    {key: 'comment'},
    {key: 'rating'},
    {key: 'signal'},
    {
        key: 'action',
        actionList: [actionTableKeys.view],
    },
]

const InitialPageable = {
    pageRows: 10,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}

const ECGHistoryTable = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }

        return parseInt(idString)
    }, [idString])

    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const type = useSignal(ECGDeviceTypes.samsung)

    const InitialFilterRequest = useMemo(
        () => ({brand: type.value, userId: id}),
        [id, type.value],
    )

    const visible = useSignal(false)
    const pageable = useSignal({...InitialPageable})
    const filterData = useSignal({
        ...InitialFilterRequest,
    })
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const ecgQueryKeys = useMemo(() => {
        const {ecgKeys} = QueryKeyManager(QueryKeyTypes.ecg, user?.id)

        return ecgKeys.page({
            pageNumber: pageable.value.pageNumber,
            pageRows: pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: filterData.value,
        })
    }, [user?.id, pageable.value, filterData.value])

    const {data, isFetched} = usePageElectrocardiograms(
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterData.value,
        true,
    )

    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()
        type.value = value
        visible.value = true
    }
    const handleHideForm = () => {
        unblockOverflownScroll()
        type.value = null
        visible.value = false
    }

    //#endregion

    return (
        <React.Fragment>
            <Dialog
                header={
                    <h5 className=" mb-0 font-bold">
                        <strong>{translateCommons('ECG History')}</strong>
                    </h5>
                }
                style={{width: '80vw'}}
                breakpoints={{'1440px': '90vw', '800px': '100vw'}}
                visible={visible.value}
                onHide={handleHideForm}>
                <StyledECGHistoryDataTable
                    brand={type.value}
                    hideForm={handleHideForm}
                />
            </Dialog>
        </React.Fragment>
    )
})

export default ECGHistoryTable
